import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, FormsModule, Validators } from '@angular/forms';
import { ProfileReview } from '@app/shared/models/ProfileReview.model';
import { ProfilesService } from '@app/shared/services/profile/profiles.service';
import { SmsService } from '@app/shared/services/sms/sms.service';
import { Subject } from 'rxjs/internal/Subject';
import { takeUntil, take, Subscription } from 'rxjs';
import { HttpResponse } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { Adb2cService } from '@app/blocks/adb2c/services/adb2c/adb2c.service';

@Component({
    selector: 'app-profile-review-modal',
    templateUrl: './profile-review-modal.component.html',
    imports: [ CommonModule, FormsModule ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfileReviewModalComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input() profileReviewModalTitle = "";
    @Input() profileReviewModalSubTitle = "";
    @Input() profileReviewModalMobileNumberLabel = "";
    @Input() profileReviewModalMobileNumberSubTitle = "";
    @Input() profileReviewModalMobileInvalidErrorMessage = "";
    @Input() profileReviewModalMobileMaxLengthErrorMessage = "";
    @Input() profileReviewModalMobileMinLengthErrorMessage = "";
    @Input() profileReviewModalVerificationCodeDescriptionLabel = "";
    @Input() profileReviewModalVerificationCodeSubTitle = "";
    @Input() profileReviewModalOtherNumberLabel = "";
    @Input() profileReviewModalOtherNumberSubTitle = "";
    @Input() profileReviewModalOtherNumberInvalidErrorMessage = "";
    @Input() profileReviewModalOtherNumberMaxLengthErrorMessage = "";
    @Input() profileReviewModalOtherNumberMinLengthErrorMessage = "";
    @Input() profileReviewModalSendVerificationCodeButton = "";
    @Input() profileReviewModalVerificationFailedMessage = "";
    @Input() profileReviewModalVerificationSuccessMessage = "";
    @Input() profileReviewModalSkipButton = "";
    @Input() profileReviewModalSubmitButton = "";

    @ViewChild('mobileNumberInput') mobileNumberInput!: FormControl;
    @ViewChild('otherNumberInput') otherNumberInput!: FormControl;

    mobilePattern = /^(\+44\s?0*7\d{3}|\(\+44\)\s?0*7\d{3}|\(?07\d{3}\)?)\s?\d{3}\s?\d{3}$/gi;

    // eslint-disable-next-line no-useless-escape
    phonePattern = /^(?:(?:\(?(?:0(?:0|11)\)?[\s-]?\(?|\+)44\)?[\s-]?(?:\(?0\)?[\s-]?)?)|(?:\(?0))(?:(?:\d{5}\)?[\s-]?\d{4,5})|(?:\d{4}\)?[\s-]?(?:\d{5}|\d{3}[\s-]?\d{3}))|(?:\d{3}\)?[\s-]?\d{3}[\s-]?\d{3,4})|(?:\d{2}\)?[\s-]?\d{4}[\s-]?\d{4}))(?:[\s-]?(?:x|ext\.?|\#)\d{3,4})?$/;

    error = false;

    formData: any = {
        mobileNumber: '',
        smsVerifyCode: '',
        otherNumber: ''
    };
    profileReviewForm: any;

    userId: string | null = null;
    mobileNumber: string | null = null;
    otherNumber: string | null = null;
    reviewDate: Date | null = null;
    mobileNumberInvalid = true;

    codeSent = false;
    // smsVerifyCode = null;
    showFailedMessage = false;
    showVerifiedMessage = false;
    smsVerifyAttempt = 1;
    smsVerifyMaxAttempts = 5

    //workaround to update ngform validation outside of its scope
    mobileNumberInputInvalid: boolean | null = true;
    otherNumberInputInvalid: boolean | null = true;

    mobileNumberValidationMessage = '';
    otherNumberValidationMessage = '';

    //last = highest
    errorPriority: string[] = [ 'maxlength', 'minlength', 'pattern' ];


    //submitting?  submit+skip
    private destroy$ = new Subject<void>();
    private userSubscription?: Subscription;

    constructor(private adb2cService: Adb2cService,
        private profilesService: ProfilesService,
        private smsService: SmsService,
        private el: ElementRef) { }

    ngOnInit(): void {
        const cpt = this.el.nativeElement;
        this.profileReviewModalTitle = cpt.getAttribute("profileReviewModalTitle");
        this.profileReviewModalSubTitle = cpt.getAttribute("profileReviewModalSubTitle");
        this.profileReviewModalMobileNumberLabel = cpt.getAttribute("profileReviewModalMobileNumberLabel");
        this.profileReviewModalMobileNumberSubTitle = cpt.getAttribute("profileReviewModalMobileNumberSubTitle");
        this.profileReviewModalMobileInvalidErrorMessage = cpt.getAttribute("profileReviewModalMobileInvalidErrorMessage");
        this.profileReviewModalMobileMaxLengthErrorMessage = cpt.getAttribute("profileReviewModalMobileMaxLengthErrorMessage");
        this.profileReviewModalMobileMinLengthErrorMessage = cpt.getAttribute("profileReviewModalMobileMinLengthErrorMessage");
        this.profileReviewModalVerificationCodeDescriptionLabel = cpt.getAttribute("profileReviewModalVerificationCodeDescriptionLabel");
        this.profileReviewModalVerificationCodeSubTitle = cpt.getAttribute("profileReviewModalVerificationCodeSubTitle");
        this.profileReviewModalOtherNumberLabel = cpt.getAttribute("profileReviewModalOtherNumberLabel");
        this.profileReviewModalOtherNumberSubTitle = cpt.getAttribute("profileReviewModalOtherNumberSubTitle");
        this.profileReviewModalOtherNumberInvalidErrorMessage = cpt.getAttribute("profileReviewModalOtherNumberInvalidErrorMessage");
        this.profileReviewModalOtherNumberMaxLengthErrorMessage = cpt.getAttribute("profileReviewModalOtherNumberMaxLengthErrorMessage");
        this.profileReviewModalOtherNumberMinLengthErrorMessage = cpt.getAttribute("profileReviewModalOtherNumberMinLengthErrorMessage");
        this.profileReviewModalSendVerificationCodeButton = cpt.getAttribute("profileReviewModalSendVerificationCodeButton");
        this.profileReviewModalVerificationFailedMessage = cpt.getAttribute("profileReviewModalVerificationFailedMessage");
        this.profileReviewModalVerificationSuccessMessage = cpt.getAttribute("profileReviewModalVerificationSuccessMessage");
        this.profileReviewModalSkipButton = cpt.getAttribute("profileReviewModalSkipButton");
        this.profileReviewModalSubmitButton = cpt.getAttribute("profileReviewModalSubmitButton");

        this.profileReviewForm = new FormGroup({
            mobileNumberInput: new FormControl('', Validators.required),

        });

        this.adb2cService.getUser();
    }

    ngAfterViewInit() {
        this.userSubscription = this.adb2cService.activeUser.asObservable().pipe(takeUntil(this.destroy$)).subscribe(user => {
            const userId = user?.sub ?? user?.oid;
            if (user && userId) {
                this.userId = userId;

                this.profilesService.getReviewFields(userId).pipe(takeUntil(this.destroy$)).subscribe((p: ProfileReview) => {
                    this.otherNumber = p.homeNumber;
                    this.mobileNumber = p.mobileNumber;

                    if (p.reviewDate !== null) {
                        this.reviewDate = new Date(p.reviewDate);
                    }
                    else {
                        this.reviewDate = null;
                    }

                    if (this.reviewDate !== null && (this.reviewDate <= new Date())) {
                        $('#profileReviewModal').modal({
                            backdrop: 'static',
                            keyboard: false,
                            show: true
                        });
                    }
                });
            }
        });
    }

    submit() {
        this.smsService.verifySmsCode(this.formData.mobileNumber.replace("+", ""), this.formData.smsVerifyCode)
            .pipe(take(1), takeUntil(this.destroy$)).subscribe((response: HttpResponse<any>) => {
                console.log(response.status);
                if (response.status === 200) {
                    this.smsVerifyAttempt = 1;
                    this.showVerifiedMessage = true;
                    this.showFailedMessage = false;

                    setTimeout(() => {
                        if (this.formData.mobileNumber) {
                            this.updateProfile(this.formData.otherNumber, this.formData.mobileNumber.replace("+", ""), true, null);
                            $('#profileReviewModal').modal('hide');
                        }
                        else {
                            throw new Error("contact numbers changed or invalid");
                        }

                    }, 3000);
                }
                else {
                    this.showVerifiedMessage = false;
                    this.showFailedMessage = true;

                    // Reset after max incorrect attempts
                    if (this.smsVerifyAttempt >= this.smsVerifyMaxAttempts) {
                        this.codeSent = false;
                        this.smsVerifyAttempt = 1;

                        ///////
                        $('#sendVerifyCodeBtn').html('Resend code?');
                        $('#smsVerifyCode').val('');
                        //////

                        this.showVerifiedMessage = false;
                        this.showFailedMessage = false;
                    }

                    this.smsVerifyAttempt++;
                }
            })
    }

    delayProfileReview() {
        if (this.userId) {
            this.profilesService.delayProfileReview(this.userId).pipe(takeUntil(this.destroy$)).subscribe();
            $('#profileReviewModal').modal('hide');
        }
    }

    //invalidates submit
    mobileChanged() {
        if (this.mobileNumberInput) {
            this.mobileNumberInputInvalid = this.mobileNumberInput.invalid;

            const errorMap: any = {
                'minlength': this.profileReviewModalMobileMinLengthErrorMessage,
                'maxlength': this.profileReviewModalMobileMaxLengthErrorMessage,
                'pattern': this.profileReviewModalMobileInvalidErrorMessage
            }

            this.mobileNumberValidationMessage = this.getErrorMessage(this.mobileNumberInput, errorMap);
        }

        this.codeSent = false;
        this.formData.smsVerifyCode = '';
    }

    verifyCodeChanged() {
        this.showFailedMessage = false;
        this.showVerifiedMessage = false;
    }

    otherChanged() {
        if (this.otherNumberInput) {
            this.otherNumberInputInvalid = this.otherNumberInput.invalid;

            const errorMap: any = {
                'minlength': this.profileReviewModalOtherNumberMinLengthErrorMessage,
                'maxlength': this.profileReviewModalOtherNumberMaxLengthErrorMessage,
                'pattern': this.profileReviewModalOtherNumberInvalidErrorMessage
            }

            this.otherNumberValidationMessage = this.getErrorMessage(this.otherNumberInput, errorMap);
        }
    }

    //validates submit
    sendVerificationCode() {
        if (this.mobileNumberInput && this.mobileNumberInput.valid) {
            this.codeSent = true;
            this.smsService.sendVerificationCode(this.formData.mobileNumber.replace("+", "")).pipe(takeUntil(this.destroy$)).subscribe();
            console.log("sending verification code", this.formData.mobileNumber.replace("+", ""));
        }
    }

    // Add new fields as and when they are required by the business. Related to task TRAN-105
    updateProfile(otherNumber: string, mobilenumber: string, mobileValidated: boolean, profileReviewDate: any): void {
        if (this.userId === null) {
            throw new Error("no userId!")
        }

        const profile = {
            HomePhoneNumber: otherNumber,
            MobilePhoneNumber: mobilenumber,
            MobileValidated: mobileValidated,
            ProfileReviewDate: profileReviewDate
        };

        this.profilesService.updateProfile(this.userId, profile).pipe(takeUntil(this.destroy$)).subscribe();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();

        this.userSubscription?.unsubscribe();
    }

    getErrorMessage(input: FormControl, errorMap: any): string {
        let errorMessage = '';

        this.errorPriority.forEach(p => {
            if (input.hasError(p)) {
                errorMessage = errorMap[ p ];
            }
        })

        return errorMessage;
    }
}
